import { useMemo, useState } from 'react';
import Seo from '@/components/feature/Seo';
import Breadcrumb from '@/components/base/Breadcrumb';
import NewsCard from '@/components/feature/NewsCard';
import { news, type NewsItem } from '@/mocks/news';

const categories = ['全部', '最新动态', '活动报道', '媒体采访', '作品发布'] as const;
type Category = (typeof categories)[number];

const PAGE_SIZE = 6;

export default function NewsList() {
  const [category, setCategory] = useState<Category>('全部');
  const [sortDesc, setSortDesc] = useState(true);
  const [page, setPage] = useState(1);

  const filtered = useMemo(() => {
    const list: NewsItem[] = category === '全部' ? [...news] : news.filter((n) => n.category === category);
    return list.sort((a, b) =>
      sortDesc ? b.date.localeCompare(a.date) : a.date.localeCompare(b.date),
    );
  }, [category, sortDesc]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const currentPage = Math.min(page, totalPages);
  const pageItems = filtered.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const resetFilters = () => {
    setCategory('全部');
    setSortDesc(true);
    setPage(1);
  };

  return (
    <>
      <Seo
        title="深田咏美新闻中心-最新动态与活动报道｜深田咏美资讯"
        description="深田咏美新闻中心，汇集最新的深田咏美新闻、活动报道与媒体采访动态，按类型与时间分类浏览她的每一则资讯。"
        keywords="深田咏美新闻,深田咏美动态,深田咏美活动,深田咏美采访"
      />

      <section className="border-b border-background-200 bg-foreground-950">
        <div className="max-w-6xl mx-auto px-4 py-12 md:px-8 md:py-16">
          <div className="mb-5 [&_*]:text-background-300">
            <Breadcrumb items={[{ label: '首页', path: '/' }, { label: '新闻中心' }]} />
          </div>
          <h1 className="font-heading text-3xl font-black text-background-50 md:text-4xl">新闻中心</h1>
          <p className="mt-3 max-w-2xl text-sm leading-relaxed text-background-300">
            按时间与类型浏览深田咏美的相关新闻与动态，了解她的最新作品发布、活动报道与媒体采访。
          </p>
        </div>
      </section>

      <section className="bg-background-50">
        <div className="max-w-6xl mx-auto px-4 py-10 md:px-8 md:py-14">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex flex-wrap items-center gap-2">
              {categories.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => {
                    setCategory(c);
                    setPage(1);
                  }}
                  className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-medium transition-colors cursor-pointer ${
                    category === c
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-700 hover:bg-background-200'
                  }`}
                >
                  {c}
                </button>
              ))}
            </div>
            <button
              type="button"
              onClick={() => {
                setSortDesc((v) => !v);
                setPage(1);
              }}
              className="inline-flex w-fit items-center gap-2 whitespace-nowrap rounded-md border border-background-300 px-4 py-2 text-sm font-medium text-foreground-700 transition-colors hover:bg-background-100 cursor-pointer"
            >
              <i className={sortDesc ? 'ri-sort-desc' : 'ri-sort-asc'}></i>
              {sortDesc ? '最新优先' : '最早优先'}
            </button>
          </div>

          {pageItems.length > 0 ? (
            <>
              <div className="mt-8 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
                {pageItems.map((item) => (
                  <NewsCard key={item.id} item={item} />
                ))}
              </div>

              {totalPages > 1 && (
                <div className="mt-10 flex items-center justify-center gap-2">
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    aria-label="上一页"
                  >
                    <i className="ri-arrow-left-s-line"></i>
                  </button>
                  {Array.from({ length: totalPages }).map((_, i) => (
                    <button
                      key={i}
                      type="button"
                      onClick={() => setPage(i + 1)}
                      className={`h-9 min-w-9 rounded-md px-3 text-sm font-medium transition-colors cursor-pointer ${
                        currentPage === i + 1
                          ? 'bg-primary-500 text-background-50'
                          : 'border border-background-300 text-foreground-700 hover:bg-background-100'
                      }`}
                    >
                      {i + 1}
                    </button>
                  ))}
                  <button
                    type="button"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={currentPage === totalPages}
                    className="flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-700 transition-colors hover:bg-background-100 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
                    aria-label="下一页"
                  >
                    <i className="ri-arrow-right-s-line"></i>
                  </button>
                </div>
              )}
            </>
          ) : (
            <div className="mt-12 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100/60 py-16 text-center">
              <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
                <i className="ri-inbox-line text-2xl"></i>
              </span>
              <p className="mt-4 font-heading text-lg font-bold text-foreground-900">暂无相关新闻</p>
              <p className="mt-1 text-sm text-foreground-500">换个分类试试，或重置筛选条件查看全部资讯。</p>
              <button
                type="button"
                onClick={resetFilters}
                className="mt-6 inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 cursor-pointer"
              >
                <i className="ri-refresh-line"></i>
                重置筛选
              </button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}